/** Static option lists and sizing constants shared by the Story editor and its right panel. */
import { AspectRatio, KenBurnsEffect, MotionPreset, SlideTransition, TextSizePreset } from './types';

export const ASPECT_RATIOS: { value: AspectRatio; label: string; css: string }[] = [
  { value: '9:16', label: '9:16', css: '9 / 16' },
  { value: '16:9', label: '16:9', css: '16 / 9' },
  { value: '1:1', label: '1:1', css: '1 / 1' },
  { value: '4:5', label: '4:5', css: '4 / 5' },
];

export const MOTION_PRESETS: { value: MotionPreset; label: string; icon: string; desc: string }[] = [
  { value: 'documentary', label: 'Documentary', icon: '🎬', desc: 'Slow + stable zoom with smooth easing' },
  { value: 'dynamic', label: 'Dynamic', icon: '⚡', desc: 'Auto pan/zoom based on image shape' },
  { value: 'cinematic', label: 'Cinematic', icon: '🎥', desc: 'Eased zoom with handheld camera feel' },
  { value: 'custom', label: 'Custom', icon: '🎛️', desc: 'Pick individual Ken Burns effects' },
];

export const KB_EFFECTS: { value: KenBurnsEffect; label: string; icon: string }[] = [
  { value: 'zoom_in', label: 'Zoom In', icon: '🔍' },
  { value: 'zoom_out', label: 'Zoom Out', icon: '🔎' },
  { value: 'pan_left', label: 'Pan Left', icon: '⬅️' },
  { value: 'pan_right', label: 'Pan Right', icon: '➡️' },
  { value: 'pan_up', label: 'Pan Up', icon: '⬆️' },
  { value: 'pan_down', label: 'Pan Down', icon: '⬇️' },
  { value: 'slow_zoom', label: 'Slow Zoom', icon: '🔭' },
  { value: 'random', label: 'Random', icon: '🎲' },
  { value: 'none', label: 'None', icon: '📌' },
];

export const TRANSITIONS: { value: SlideTransition; label: string }[] = [
  { value: 'none', label: 'None (Cut)' },
  { value: 'fade', label: 'Fade' },
  { value: 'crossfade', label: 'Crossfade' },
  { value: 'fadeblack', label: 'Fade to Black' },
  { value: 'fadewhite', label: 'Fade to White' },
  { value: 'fadegrays', label: 'Fade to Gray' },
  { value: 'slide_left', label: 'Slide Left' },
  { value: 'slide_right', label: 'Slide Right' },
  { value: 'slideleft', label: 'Slide Left (xfade)' },
  { value: 'slideright', label: 'Slide Right (xfade)' },
  { value: 'slideup', label: 'Slide Up' },
  { value: 'slidedown', label: 'Slide Down' },
  { value: 'zoom', label: 'Zoom' },
  { value: 'zoomin', label: 'Zoom In' },
  { value: 'dissolve', label: 'Dissolve' },
  { value: 'wipeleft', label: 'Wipe Left' },
  { value: 'wiperight', label: 'Wipe Right' },
  { value: 'wipeup', label: 'Wipe Up' },
  { value: 'wipedown', label: 'Wipe Down' },
  { value: 'wipetl', label: 'Wipe Top-Left' },
  { value: 'wipetr', label: 'Wipe Top-Right' },
  { value: 'wipebl', label: 'Wipe Bottom-Left' },
  { value: 'wipebr', label: 'Wipe Bottom-Right' },
  { value: 'circleopen', label: 'Circle Open' },
  { value: 'circleclose', label: 'Circle Close' },
  { value: 'rectcrop', label: 'Rect Crop' },
  { value: 'circlecrop', label: 'Circle Crop' },
  { value: 'radial', label: 'Radial' },
  { value: 'diagtl', label: 'Diagonal TL' },
  { value: 'diagtr', label: 'Diagonal TR' },
  { value: 'diagbl', label: 'Diagonal BL' },
  { value: 'diagbr', label: 'Diagonal BR' },
  { value: 'pixelize', label: 'Pixelize' },
  { value: 'smoothleft', label: 'Smooth Left' },
  { value: 'smoothright', label: 'Smooth Right' },
  { value: 'smoothup', label: 'Smooth Up' },
  { value: 'smoothdown', label: 'Smooth Down' },
  { value: 'horzopen', label: 'Horizontal Open' },
  { value: 'horzclose', label: 'Horizontal Close' },
  { value: 'vertopen', label: 'Vertical Open' },
  { value: 'vertclose', label: 'Vertical Close' },
  { value: 'squeezeh', label: 'Squeeze Horizontal' },
  { value: 'squeezev', label: 'Squeeze Vertical' },
  { value: 'hlslice', label: 'HL Slice' },
  { value: 'hrslice', label: 'HR Slice' },
  { value: 'vuslice', label: 'VU Slice' },
  { value: 'vdslice', label: 'VD Slice' },
];

export const EMOJI_PRESETS = ['🔥', '✨', '💯', '🎯', '🚀', '❤️', '👏', '😮', '🤯', '💡', '⭐', '🎬', '📸', '🌟', '💪', '🎵', '🌈', '💎'];

export const DEFAULT_CAPTION_BOX = { bgWidth: 38, bgHeight: 8 };
export const DEFAULT_NARRATION_BOX = { bgWidth: 48, bgHeight: 10 };

export const CAPTION_FONTS = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins',
  'Oswald', 'Bebas Neue', 'Anton', 'Arial', 'Georgia', 'Impact',
];

// Percentages of frame height, matching how Kinetic Caption authors size. Normal is its 6%
// default; the headings keep the same ratios above it. At 1920px tall: 115 / 173 / 240px.
export const TEXT_SIZE_PRESETS: { value: TextSizePreset; label: string; pct: number | null }[] = [
  { value: 'normal', label: 'Normal Text', pct: 6 },
  { value: 'h1', label: 'Heading 1', pct: 12.5 },
  { value: 'h2', label: 'Heading 2', pct: 9 },
  { value: 'custom', label: 'Custom size', pct: null },
];

export const FONT_PCT_MIN = 2;
export const FONT_PCT_MAX = 15;
export const FONT_PCT_STEP = 0.5;

// Legacy overlays stored absolute px authored against a 1080x1920 canvas.
export const legacyPxToPct = (px: number | undefined, fallback: number) =>
  typeof px === 'number' && px > 0
    ? Math.min(FONT_PCT_MAX, Math.max(FONT_PCT_MIN, (px / 1920) * 100))
    : fallback;

export const DEFAULT_TEXT_STYLE = {
  fontFamily: 'Inter',
  sizePreset: 'normal' as TextSizePreset,
  fontSizePct: 6,
  bold: false,
  italic: false,
  underline: false,
  color: '#ffffff',
  bgColor: null as string | null,
};


// ── Clip slides ──────────────────────────────────────────────
export const PLAYBACK_SPEEDS: { value: number; label: string }[] = [
  { value: 0.25, label: '0.25×' },
  { value: 0.5, label: '0.5×' },
  { value: 0.75, label: '0.75×' },
  { value: 1, label: 'Normal' },
  { value: 1.25, label: '1.25×' },
  { value: 1.5, label: '1.5×' },
  { value: 2, label: '2×' },
];

/** Extensions the slide picker accepts for a clip slide. */
export const VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/webm,video/x-matroska';

// ── Story-level settings ─────────────────────────────────────
export const PLATFORMS: { value: string; label: string }[] = [
  { value: 'youtube_shorts', label: 'YouTube Shorts' },
  { value: 'instagram_reels', label: 'Instagram Reels' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'twitter', label: 'X / Twitter' },
];

export const AUDIENCES: { value: string; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'business', label: 'Business' },
  { value: 'consumer', label: 'Consumer' },
  { value: 'tech', label: 'Tech' },
];

export const STORY_DESCRIPTION_MAX = 500;
